import { useApplePayDialog } from '@cart-services/hooks/useApplePayDialog'
import { type ApplePayDialogContext } from '@cart-services/hooks/useApplePayDialogActions'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { Box } from '@pets-ui/Box/Box'
import { Button } from '@pets-ui/Button/Button'
import { CircularProgress } from '@pets-ui/CircularProgress/CircularProgress'
import { Dialog } from '@pets-ui/Dialog/Dialog'
import { DialogActions } from '@pets-ui/DialogActions/DialogActions'
import { DialogContent } from '@pets-ui/DialogContent/DialogContent'
import { DialogHeader } from '@pets-ui/DialogHeader/DialogHeader'
import { Drawer } from '@pets-ui/Drawer/Drawer'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { useResponsive } from '@pets-ui/useResponsive/useResponsive'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { type CSSProperties, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DialogPaymentLeaveConfirm } from '../DialogPaymentLeaveConfirm/DialogPaymentLeaveConfirm'
import cartUiI18nInstance from '../i18n/i18nConfig'

const MOUNT_MIN_HEIGHT_PX = 48

const styles = stylex.create({
  mount: {
    minHeight: MOUNT_MIN_HEIGHT_PX,
    width: '100%',
  },
  mountHidden: {
    display: 'none',
  },
  loadingArea: {
    padding: '32px 0',
    display: 'flex',
    justifyContent: 'center',
  },
  contentNoBottomPadding: {
    paddingBottom: 0,
  },
  drawerPaper: {
    borderRadius: '8px 8px 0px 0px',
  },
  cancelButton: {
    height: 40,
  },
})

// Apple Pay button width is driven by a CSS custom property, not `width`.
const mountInlineStyle = { '--apple-pay-button-width': '100%' } as CSSProperties

export interface DialogApplePayConfirmProps {
  context: ApplePayDialogContext
  onClose: () => void
  mountApplePayButton: (context: ApplePayDialogContext, containerId: string) => Promise<void>
  awaitAndExecutePayment: (context: ApplePayDialogContext) => Promise<void>
  cancelInFlight: (reason: string) => void
  onError?: (error: unknown) => void
  /** 授權完成後 execution 進行中；此時 cancelInFlight 為 no-op，以 loading 狀態取代取消按鈕 */
  isExecuting?: boolean
  /** 使用者確認離開付款時呼叫（離開確認框點「離開」）。 */
  onCancel?: () => void
  /** 覆寫 dialog 內的 generic 錯誤文字（如 CKO-specific 錯誤訊息）。 */
  errorMessage?: string
}

export const DialogApplePayConfirm = ({ context, onClose, mountApplePayButton, awaitAndExecutePayment, cancelInFlight, onError, isExecuting = false, onCancel, errorMessage }: DialogApplePayConfirmProps) => {
  const responsiveIsMobile = useResponsive('mobile')
  /** 鎖定初次 render 的斷點：resize 跨 breakpoint 若重建 Drawer/Dialog subtree 會拆掉已掛載的 SDK 容器，此處無 remount 機制可回復。 */
  const [isMobile] = useState(responsiveIsMobile)
  const { isMounting, hasError, containerId, cancel, retry } = useApplePayDialog({ context, mountApplePayButton, awaitAndExecutePayment, cancelInFlight, onError })
  const { t } = useTranslation('cartUi', { i18n: cartUiI18nInstance })
  const [isLeaveConfirmOpen, setIsLeaveConfirmOpen] = useState(false)

  const handleCloseAttempt = useCallbackEvent(() => {
    setIsLeaveConfirmOpen(true)
  })

  const handleStay = useCallbackEvent(() => {
    setIsLeaveConfirmOpen(false)
  })

  const handleLeave = useCallbackEvent(() => {
    setIsLeaveConfirmOpen(false)
    onCancel?.()
    cancel('user closed dialog')
    onClose()
  })

  const dialogInner = (
    <>
      <DialogHeader align="center" title={t('cartUi.applePayConfirmTitle', { defaultValue: '使用 Apple Pay 完成付款' })} />
      <DialogContent rootStyle={styles.contentNoBottomPadding}>
        <VStack gap="16px">
          {isMounting
            ? (
              <div {...stylex.props(styles.loadingArea)}>
                <CircularProgress fontSize="32px" color={colors.grey500} />
              </div>
            )
            : (
              <Text variant="body" color={colors.grey700}>
                {t('cartUi.applePayCompleteOnDevice', { defaultValue: '請按下下方 Apple Pay 按鈕，在裝置上完成付款驗證。' })}
              </Text>
            )
          }
          <div
            id={containerId}
            data-testid={containerId}
            {...stylex.props(styles.mount, (isMounting || hasError) && styles.mountHidden)}
            style={mountInlineStyle}
          />
          {!isMounting && hasError && (
            <Text variant="subtitle" color={colors.red500}>
              {errorMessage ?? t('cartUi.paymentErrorGeneric', { defaultValue: '付款發生問題，請稍後再試或選擇其他付款方式。' })}
            </Text>
          )}
        </VStack>
      </DialogContent>
      <DialogActions>
        {isExecuting && !hasError
          ? (
            <Box display="flex" justifyContent="center" width="100%" py={2}>
              <CircularProgress fontSize="32px" color={colors.grey500} />
            </Box>
          )
          : !isMounting && (hasError
            ? (
              <>
                <Button variant="outlined" onClick={onClose} fullWidth>
                  {t('cartUi.changePaymentMethod', { defaultValue: '更換付款方式' })}
                </Button>
                <Button onClick={retry} fullWidth>
                  {t('cartUi.retryPayment', { defaultValue: '重新再試' })}
                </Button>
              </>
            )
            : (
              <Button variant="outlined" color="grey" onClick={handleCloseAttempt} disabled={isLeaveConfirmOpen} fullWidth rootStyle={styles.cancelButton}>
                {t('cartUi.cancelPayment', { defaultValue: '取消付款' })}
              </Button>
            )
          )
        }
      </DialogActions>
    </>
  )

  return (
    <>
      {isMobile
        ? (
          <Drawer open direction="bottom" height="auto" xStyles={{ base: { paper: styles.drawerPaper } }}>
            {dialogInner}
          </Drawer>
        )
        : (
          <Dialog open fullWidth maxWidth={480} height="auto">
            {dialogInner}
          </Dialog>
        )}
      {isLeaveConfirmOpen && <DialogPaymentLeaveConfirm onStay={handleStay} onLeave={handleLeave} />}
    </>
  )
}
